import React, { useEffect, useMemo, useState } from "react";
import { AssetsService } from "../../api/generated";
import { withApiData } from "../../api/openapiClient";
import { useAuth } from "../../useAuth";
import { RoleEnum, isRole } from "../../constants/roles";
import Loader from "../../components/common/Loader";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Table from "../../ui/Table";
import { spacing } from "../../ui/tokens";

const emptyCardStyle = {
  border: "1px dashed #d1d5db",
  borderRadius: 12,
  padding: spacing.xl,
  background: "#f9fafb",
  display: "grid",
  gap: spacing.sm,
  maxWidth: 520,
};

export default function AssetsRegister() {
  const { user } = useAuth();
  const canEdit = isRole(user?.role, RoleEnum.ADMIN);

  const [assets, setAssets] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const loadAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withApiData(AssetsService.listAssetsAssetsGet());
      setAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    const handler = () => setShowForm(true);
    window.addEventListener("openAddAsset", handler);
    return () => window.removeEventListener("openAddAsset", handler);
  }, []);

  const filteredAssets = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return assets;
    return assets.filter((asset) => {
      const nameMatch = asset.name?.toLowerCase().includes(term);
      const ownerMatch = asset.owner?.toLowerCase().includes(term);
      return nameMatch || ownerMatch;
    });
  }, [assets, query]);

  const columns = useMemo(
    () => [
      { header: "Name", accessor: "name" },
      {
        header: "Owner",
        accessor: (row) => row.owner || "Unassigned",
      },
    ],
    []
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setSaveError(null);
    try {
      await withApiData(
        AssetsService.createAssetAssetsPost({
          requestBody: {
            name: trimmed,
            owner: owner.trim() || null,
          },
        })
      );
      setName("");
      setOwner("");
      setShowForm(false);
      await loadAssets();
    } catch (err) {
      setSaveError(err?.message || "Failed to create asset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: spacing.lg }}>
      <div>
        <h2 style={{ marginBottom: 6 }}>Assets</h2>
        <p style={{ marginTop: 0, color: "#6b7280", fontSize: 14 }}>
          Track systems, data stores, and critical services that controls protect.
        </p>
      </div>

      <div style={{ display: "flex", gap: spacing.md, flexWrap: "wrap", alignItems: "center" }}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search assets"
          style={{ minWidth: 220 }}
        />
        <Button variant="secondary" onClick={loadAssets}>
          Refresh
        </Button>
        {canEdit && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            Add Asset
          </Button>
        )}
      </div>

      {canEdit && showForm && (
        <form
          onSubmit={handleCreate}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: spacing.lg,
            display: "grid",
            gap: spacing.md,
            background: "#ffffff",
            maxWidth: 640,
          }}
        >
          <div style={{ fontWeight: 600 }}>Add a new asset</div>
          <div style={{ display: "grid", gap: spacing.sm }}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Asset name"
            />
            <Input
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Owner or team"
            />
          </div>
          {saveError && <div style={{ color: "#b91c1c" }}>{saveError}</div>}
          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button type="submit" disabled={!name.trim()} loading={saving}>
              Create Asset
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {loading && <Loader label="Loading assets..." />}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}

      {!loading && !error && assets.length === 0 && (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 600 }}>No assets yet</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Assets show what needs coverage so controls can be linked to the right systems.
          </div>
          {canEdit ? (
            <Button onClick={() => setShowForm(true)}>Add your first asset</Button>
          ) : (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Contact an administrator to add assets.
            </div>
          )}
        </div>
      )}

      {!loading && !error && assets.length > 0 && (
        <Table
          columns={columns}
          data={filteredAssets}
          emptyState="No matching assets."
          getRowKey={(row) => row.id}
        />
      )}
    </div>
  );
}
